import React, { useContext, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { initScrollHandler } from "./utils/scrollHandler";
import { RecruiterProvider } from "./context/RecruiterContext";
import { AuthContext } from "./context/AuthContext";
import InactivityMonitor from "./components/common/InactivityMonitor";
import ActivityTracker from "./components/common/ActivityTracker";
import { isEmployerRoute } from "./config/constants";
import CandidateHomePage from "./pages/CandidateHomePage";
import JobsPage from "./pages/JobsPage";
import CandidateSignupPage from "./pages/CandidateSignupPage";
import CandidateAuthPage from "./pages/CandidateAuthPage";
import EmployerAuthPage from "./pages/EmployerAuthPage";
import EmployerDashboardPage from "./pages/EmployerDashboardPage";
// CandidateDashboardPage removed - redirecting to JobsPage
import JobDetailPage from "./pages/JobDetailPage";
import PostJobPage from "./pages/PostJobPage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import CandidateInboxPage from "./pages/InboxPage";
import EmployerInboxPage from "./pages/EmployerInboxPage";
import PostedJobsPage from "./pages/PostedJobsPage";
import DatabasePage from "./pages/DatabasePage";
// InterviewsPage removed
import AnalyticsPage from "./pages/AnalyticsPage";
import BrandingPage from "./pages/BrandingPage";
import BillingPage from "./pages/BillingPage";
import SupportPage from "./pages/SupportPage";
import EmployerCompleteProfilePage from "./pages/EmployerCompleteProfilePage";
import CandidateCompleteProfilePage from "./pages/CandidateCompleteProfilePage";
import ApplicationsPage from "./pages/ApplicationsPage";
import PricingPage from "./pages/PricingPage";
import SavedJobsPage from "./pages/SavedJobsPage";
import JobAlertsPage from "./pages/JobAlertsPage";
// import CandidateApplicationsPage from "./pages/CandidateApplicationsPage"; // REMOVED
import PaymentPage from "./pages/PaymentPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import CandidateProfilePage from "./pages/CandidateProfilePage";
import CandidatesPage from "./pages/CandidatesPage";
import AboutUsPage from "./pages/AboutUsPage";
import ContactPage from "./pages/ContactPage";
import BlogsPage from "./pages/BlogsPage";
import Admin1866Page from "./pages/Admin1866Page";
import AdminLoginPage from "./pages/AdminLoginPage";
import RecruiterManagementPage from "./pages/RecruiterManagementPage";
import RecruiterPerformancePage from "./pages/RecruiterPerformancePage";
import RecruiterJoinPage from "./pages/RecruiterJoinPage";
import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import NotificationsPage from "./pages/NotificationsPage";
import AffiliateSignupPage from './pages/AffiliateSignupPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AffiliateAutoLogin from './pages/AffiliateAutoLogin';
import ProtectedRoute from "./components/ProtectedRoute";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import { ToastProvider } from "./components/ui/Toast";

export default function App() {
  const { isAuthenticated, user } = useContext(AuthContext);
  
  // CRITICAL: Clean tokens IMMEDIATELY on component mount
  useEffect(() => {
    const adminToken = localStorage.getItem('adminToken');
    const userToken = localStorage.getItem('authToken');
    const isOnAdminRoute = window.location.pathname.startsWith('/admin');
    const cleanupDone = sessionStorage.getItem('token_cleanup_done');
    
    // Use centralized employer route detection
    const isOnEmployerRoute = isEmployerRoute(window.location.pathname);

    console.log('[App] Token check:', {
      hasAdmin: !!adminToken,
      hasUser: !!userToken,
      isAdminRoute: isOnAdminRoute,
      isEmployerRoute: isOnEmployerRoute,
      pathname: window.location.pathname,
      cleanupDone: !!cleanupDone
    });

    // Skip cleanup for employer routes with only user token (normal employer session)
    if (isOnEmployerRoute && userToken && !adminToken) {
      console.log('[App] Employer route with only user token - skipping cleanup');
      return;
    }

    // Skip cleanup for admin routes with only admin token (normal admin session)
    if (isOnAdminRoute && adminToken && !userToken) {
      console.log('[App] Admin route with only admin token - skipping cleanup');
      return;
    }

    // CRITICAL: If admin token exists on employer route, clear it immediately (even with recent activity)
    if (adminToken && !isOnAdminRoute) {
      console.error('[App] CRITICAL: Admin token on employer route - clearing admin tokens');
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      // Force page reload to reinitialize with correct tokens
      window.location.reload();
      return;
    }
    
    // CRITICAL: If user token exists on admin route, clear it immediately (even with recent activity)
    if (userToken && isOnAdminRoute) {
      console.error('[App] CRITICAL: User token on admin route - clearing user tokens');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      // Force page reload to reinitialize with correct tokens
      window.location.reload();
      return;
    }
    
    // Check for recent user activity before doing general cleanup
    const lastUserActivity = sessionStorage.getItem('lastUserActivity');
    const now = Date.now();
    const recentActivity = lastUserActivity && (now - parseInt(lastUserActivity)) < 300000; // 5 minutes
    
    if (recentActivity) {
      console.log('[App] Skipping cleanup due to recent user activity', {
        timeSinceActivity: now - parseInt(lastUserActivity),
        pathname: window.location.pathname
      });
      return;
    }
    
    // ONLY clean if BOTH tokens exist (conflict situation)
    if (adminToken && userToken && !cleanupDone) {
      console.error('[App] CONFLICT: Both tokens exist! Cleaning up...', {
        pathname: window.location.pathname,
        isAdminRoute: isOnAdminRoute,
        isEmployerRoute: isOnEmployerRoute
      });
      
      if (isOnAdminRoute) {
        console.error('[App] On admin route - clearing user tokens');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      } else {
        console.error('[App] On user/employer route - clearing admin tokens');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
      sessionStorage.setItem('token_cleanup_done', 'true');
      // No page reload - cleanup is complete, let app continue
      console.log('[App] Cleanup complete - continuing without reload');
    } else {
      console.log('[App] No conflict detected - session is clean');
    }
  }, []);
  
  // Initialize scroll handler to show scrollbar only when scrolling
  useEffect(() => {
    const cleanup = initScrollHandler();
    return cleanup;
  }, []);

  return (
    <ToastProvider>
      {/* Add InactivityMonitor for authenticated users */}
      {isAuthenticated && <InactivityMonitor />}
      
      {/* Add ActivityTracker for performance monitoring */}
      <ActivityTracker />
      
      <Routes>
      {/* Public routes */}
      <Route path="/" element={<CandidateHomePage />} />
      <Route path="/jobs" element={<JobsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/blogs" element={<BlogsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/auth/candidate" element={<CandidateAuthPage />} />
      <Route path="/auth/employer" element={<EmployerAuthPage />} />
      <Route path="/candidate/signup" element={<CandidateSignupPage />} />
      <Route path="/recruiter/join" element={<RecruiterJoinPage />} />
      <Route path="/payment" element={<PaymentPage />} />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Affiliate routes */}
      <Route path="/affiliate/signup" element={<AffiliateSignupPage />} />
      <Route path="/affiliates/auth" element={<AffiliateAutoLogin />} />
      <Route path="/affiliate/dashboard" element={<ProtectedRoute allowedUserTypes={["affiliate"]}><AffiliateDashboardPage /></ProtectedRoute>} />
      
      {/* Employer public routes */}
      <Route path="/candidates" element={<CandidatesPage />} />
      <Route path="/candidates/:candidateId" element={<ProtectedRoute allowedUserTypes={["employer", "recruiter"]}><CandidateProfilePage /></ProtectedRoute>} />
      
      {/* ===== CANDIDATE ROUTES ===== */}
      {/* Candidate profile completion */}
      <Route path="/candidate/complete-profile" element={<ProtectedRoute allowedUserTypes={["candidate"]}><CandidateCompleteProfilePage /></ProtectedRoute>} />
      
      {/* Candidate protected routes - redirect to jobs page */}
      <Route 
        path="/candidate/dashboard" 
        element={<Navigate to="/jobs" replace />}
      />
      <Route 
        path="/candidate/jobs" 
        element={<Navigate to="/jobs" replace />}
      />
      <Route 
        path="/candidate/jobs/:jobId" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><JobDetailPage /></ProtectedRoute>}
      />
      {/* REMOVED: Applications Page */}
      {/* <Route 
        path="/candidate/applications" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><CandidateApplicationsPage /></ProtectedRoute>}
      /> */}
      <Route 
        path="/candidate/saved-jobs" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><SavedJobsPage /></ProtectedRoute>}
      />
      <Route 
        path="/candidate/messages" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><CandidateInboxPage /></ProtectedRoute>}
      />
      <Route 
        path="/candidate/profile" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><ProfileSettingsPage /></ProtectedRoute>}
      />
      <Route 
        path="/candidate/resume" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><ProfileSettingsPage /></ProtectedRoute>}
      />
      <Route 
        path="/candidate/job-alerts" 
        element={<ProtectedRoute allowedUserTypes={["candidate"]}><JobAlertsPage /></ProtectedRoute>}
      />
      {/* Company Reviews page removed */}
      {/* Support page for candidates removed */}
      
      {/* ===== EMPLOYER ROUTES ===== */}
      {/* Employer profile completion */}
      <Route path="/complete-profile" element={<ProtectedRoute allowedUserTypes={["employer"]}><EmployerCompleteProfilePage /></ProtectedRoute>} />
      <Route path="/employer/complete-profile" element={<ProtectedRoute allowedUserTypes={["employer"]}><EmployerCompleteProfilePage /></ProtectedRoute>} />
      
      {/* Employer protected routes */}
      <Route 
        path="/dashboard" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><EmployerDashboardPage /></ProtectedRoute>}
      />
      <Route 
        path="/employer/dashboard" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><EmployerDashboardPage /></ProtectedRoute>}
      />
      <Route 
        path="/post-job" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><PostJobPage /></ProtectedRoute>}
      />
      <Route 
        path="/profile" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><ProfileSettingsPage /></ProtectedRoute>}
      />
      <Route 
        path="/inbox/:conversationId?" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><EmployerInboxPage /></ProtectedRoute>}
      />
      <Route 
        path="/posted-jobs" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><PostedJobsPage /></ProtectedRoute>}
      />
      <Route 
        path="/database" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><DatabasePage /></ProtectedRoute>}
      />
      {/* Interviews route removed */}
      <Route 
        path="/analytics" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><AnalyticsPage /></ProtectedRoute>}
      />
      <Route 
        path="/branding" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><BrandingPage /></ProtectedRoute>}
      />
      <Route 
        path="/billing" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><BillingPage /></ProtectedRoute>}
      />
      <Route 
        path="/support" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><SupportPage /></ProtectedRoute>}
      />
      <Route 
        path="/notifications" 
        element={<ProtectedRoute allowedUserTypes={["employer", "recruiter"]}><NotificationsPage /></ProtectedRoute>}
      />
      <Route 
        path="/applications" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><ApplicationsPage /></ProtectedRoute>}
      />
      <Route 
        path="/recruiters" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><RecruiterProvider><RecruiterManagementPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter-performance" 
        element={<ProtectedRoute allowedUserTypes={["employer"]}><RecruiterProvider><RecruiterPerformancePage /></RecruiterProvider></ProtectedRoute>}
      />
      
      {/* Recruiter Routes */}
      <Route 
        path="/recruiter/dashboard" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><EmployerDashboardPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/inbox/:conversationId?" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><EmployerInboxPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/applications" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><ApplicationsPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/database" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><DatabasePage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/posted-jobs" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><PostedJobsPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/support" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><SupportPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/profile" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><ProfileSettingsPage /></RecruiterProvider></ProtectedRoute>}
      />
      <Route 
        path="/recruiter/post-job" 
        element={<ProtectedRoute allowedUserTypes={["recruiter"]}><RecruiterProvider><PostJobPage /></RecruiterProvider></ProtectedRoute>}
      />
      
      {/* Admin Routes */}
      <Route 
        path="/admin/login" 
        element={<AdminLoginPage />}
      />
      <Route 
        path="/admin/1866" 
        element={<AdminProtectedRoute><Admin1866Page /></AdminProtectedRoute>}
      />
    </Routes>
    </ToastProvider>
  );
}
